/**
 * Deterministic daily selection and the household's puzzle date.
 *
 * The daily word is chosen client-side from the date alone, so both phones
 * compute the identical word for "today" with no server round-trip — which is
 * exactly what makes it a *shared* daily challenge. Every step must therefore be
 * bit-for-bit stable across engines and platforms — the FNV-1a seed and the
 * mulberry32 shuffle both use `Math.imul`, where a plain `* prime` would drift
 * into floating point past 2^53.
 */
/** FNV-1a, 32-bit, unsigned. Stable across every JS engine. */
function hashString(input) {
    let h = 0x811c9dc5;
    for (let i = 0; i < input.length; i += 1) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return h >>> 0;
}
/**
 * The first day of the shuffled order. Any fixed date works; it only has to
 * never move, since moving it re-maps every date nobody has played yet.
 */
const EPOCH_UTC = Date.UTC(2026, 0, 1);
const DAY_MS = 86_400_000;
/** Whole days from the epoch to a `YYYY-MM-DD` date (negative before it). */
function dayNumber(dateString) {
    const [y, m, d] = dateString.split('-').map(Number);
    return Math.round((Date.UTC(y, m - 1, d) - EPOCH_UTC) / DAY_MS);
}
/** mulberry32: a small seeded PRNG on 32-bit integer maths, so every engine agrees. */
function seededRandom(seed) {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/**
 * The index into a target list for a given date string (`YYYY-MM-DD`).
 *
 * **A shuffle walked one day at a time, not a hash per date** (2026-10-19). A
 * hash picks each day independently, so it repeats like a die: 62 repeats in
 * a year from 1,200 words (HYDRA on 19 October *and* 1 January), and a third
 * of the list never came up in three years. Here day *n* is position
 * `n mod length` of a Fisher–Yates permutation seeded by the cycle number
 * `floor(n / length)` — so every word comes up exactly once per cycle, and the
 * next cycle is a fresh order. Still pure and deterministic, so both phones
 * agree with no round-trip.
 */
export function dailyIndex(dateString, length) {
    if (length <= 0)
        return 0;
    const day = dayNumber(dateString);
    const cycle = Math.floor(day / length);
    const position = day - cycle * length;
    const order = permutation(length, hashString(`cycle:${cycle}`));
    return order[position];
}
const permutations = new Map();
function permutation(length, seed) {
    const key = `${length}:${seed}`;
    const cached = permutations.get(key);
    if (cached)
        return cached;
    const random = seededRandom(seed);
    const order = Array.from({ length }, (_, i) => i);
    for (let i = length - 1; i > 0; i -= 1) {
        const j = Math.floor(random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
    }
    permutations.set(key, order);
    return order;
}
/**
 * The calendar date (`YYYY-MM-DD`) of an instant in a timezone — the household's
 * *puzzle date*. The daily turns over at local midnight, so a phone opened at
 * 23:00 and one opened at 00:30 are on different puzzles, correctly.
 *
 * `en-CA` renders `YYYY-MM-DD`, but we assemble from parts so a locale/runtime
 * quirk can never reorder it.
 */
export function puzzleDateFor(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const get = (type) => parts.find((p) => p.type === type).value;
    return `${get('year')}-${get('month')}-${get('day')}`;
}
//# sourceMappingURL=daily.js.map